import { IconEncircledCheckmark, IconEncircledX, IconTimeClock } from '../../../../assets/DesignSystem/SVGComponents';
import {
  IOrderFinances,
  ORDER_STATUS_NAME_TRIM,
  PAYMENT_TYPES,
  BORD_CREDIT_PAYMENT_TYPES,
  TYPES_OF_BILLING
} from '../../../../types/orders';
import { formatShortDate } from '../../../../utils/formatDatesAndHours';
import './OrderPaymentInformation.scss';

interface OrderPaymentInformationProps {
  orderData: IOrderFinances;
  openPaymentLinkModalCallback?: () => void;
  isOrderDetailsView?: boolean;
  invoiceShow?: boolean;
  isCommittedPaymentAgreement?: boolean;
}

/**
 * Componente que muestra el estado de pago de una orden con iconos y colores indicativos
 * @param orderData - Datos de la orden incluyendo información de pago
 * @param openPaymentLinkModalCallback - Callback para abrir el modal de carga de link de pago
 * @param isOrderDetailsView - Indica si se muestra en la vista de detalles de la orden
 * @param invoiceShow - Indica si se debe mostrar la información de factura
 */
const OrderPaymentInformation = ({
  orderData,
  isOrderDetailsView,
  invoiceShow,
  isCommittedPaymentAgreement
}: OrderPaymentInformationProps) => {
  const { order } = orderData;
  const { invoiceRecords, committedPaymentDate, PaymentType, proofOfPaymentUrl, billingMethod, shipmentStatusLogs } =
    order;
  const { shipmentStatus } = shipmentStatusLogs?.[0] || {};
  const statusName = shipmentStatus?.name || '';
  const paymentType = PaymentType?.name || '';
  const billingProvider = billingMethod?.billingProvider?.name || '';
  const invoiceUrl = invoiceRecords?.[0]?.invoiceUrl || '';
  const uploadedInvoiceDate = invoiceRecords?.[0]?.invoiceEmissionDate || '';
  const dateToday = new Date();
  const todayISO = dateToday.toISOString().split('T')[0];
  const commitedDateISO = committedPaymentDate ? committedPaymentDate?.split('T')[0] : '';
  const isPaymentAgreementExpired = commitedDateISO ? todayISO > commitedDateISO : false;
  const orderIsCanceled = statusName?.toLowerCase().trim() === ORDER_STATUS_NAME_TRIM.CANCELLED;
  const orderAlreadyDelivered = statusName?.toLowerCase().trim() === ORDER_STATUS_NAME_TRIM.DELIVERED;
  const isPaymentTypeBordCredit = paymentType
    ? BORD_CREDIT_PAYMENT_TYPES.includes(paymentType as PAYMENT_TYPES)
    : false;
  const hasPaymentAgreement = !!committedPaymentDate;
  const isPaymentReceived = !!proofOfPaymentUrl;
  const isPaymentAgreementWithCreditBord = isPaymentTypeBordCredit;
  const isPayedWithNudosCare = paymentType === 'Care';
  const isPayedWithNudosCareAndOrderDetailsView = isOrderDetailsView && isPayedWithNudosCare;
  const isPaymentAutomatic =
    billingProvider &&
    !committedPaymentDate &&
    [TYPES_OF_BILLING.GIGSTACK, TYPES_OF_BILLING.STRIPE_USA_NUDOS_LLC, TYPES_OF_BILLING.STRIPE_USA_NUDOS_CORP].includes(
      billingProvider as TYPES_OF_BILLING
    );

  /**
   * Determina y retorna los datos de visualización del estado de pago
   * @returns Objeto con icono, texto y clase CSS para mostrar el estado de pago
   */
  const getPaymentDisplayData = () => {
    const formattedPaymentDate = committedPaymentDate ? formatShortDate(committedPaymentDate)?.onlyDate : null;
    const hasCreditBordAgreement =
      hasPaymentAgreement && isPaymentAgreementWithCreditBord && isCommittedPaymentAgreement;
    const hasAnyPaymentAgreement = hasPaymentAgreement && isCommittedPaymentAgreement;
    const isPendingPaymentWithCreditBordAgreement = !isPaymentReceived && hasCreditBordAgreement;
    const isPaymentReceivedWithCreditBordAgreement = isPaymentReceived && hasCreditBordAgreement;
    const isPaymentReceivedWithAnyAgreement = isPaymentReceived && hasAnyPaymentAgreement && !hasCreditBordAgreement;
    const isPendingPaymentWithAnyAgreement = !isPaymentReceived && hasAnyPaymentAgreement && !hasCreditBordAgreement;
    const showPaidLabel = isPaymentAutomatic || isPaymentAgreementWithCreditBord;

    const paymentStatusCases = [
      {
        condition: orderIsCanceled,
        result: {
          icon: <IconEncircledX className="IconEncircledX" />,
          text: 'Cancelado',
          classname: 'red'
        }
      },
      {
        condition: orderAlreadyDelivered && !isCommittedPaymentAgreement,
        result: {
          icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
          text: 'Entregado',
          classname: 'green'
        }
      },
      {
        condition: isPayedWithNudosCare,
        result: {
          icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
          text: 'Cubierto con Nudos Care',
          classname: 'green'
        }
      },
      {
        condition: isPaymentReceived,
        result: {
          icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
          text: showPaidLabel ? 'Pagado' : 'Comprobante cargado',
          classname: 'green'
        }
      },
      {
        condition: isPaymentReceivedWithCreditBordAgreement || isPaymentReceivedWithAnyAgreement,
        result: {
          icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
          text: 'Pagado',
          classname: 'green'
        }
      },
      {
        condition: isPendingPaymentWithCreditBordAgreement,
        result: {
          icon: <IconTimeClock className="IconTimeClock" />,
          text: formattedPaymentDate ? `Para el ${formattedPaymentDate}` : 'Sin fecha de acuerdo',
          classname: isPaymentAgreementExpired ? 'red' : 'blue'
        }
      },
      {
        condition: isPendingPaymentWithAnyAgreement,
        result: {
          icon: <IconTimeClock className="IconTimeClock" />,
          text: formattedPaymentDate ? `Para el ${formattedPaymentDate}` : 'Sin fecha de acuerdo',
          classname: isPaymentAgreementExpired ? 'red' : 'blue'
        }
      }
    ];

    const matchedCase = paymentStatusCases.find(caseItem => caseItem.condition);
    return (
      matchedCase?.result || {
        icon: <IconTimeClock className="IconTimeClock" />,
        text: 'Pendiente de pago',
        classname: ''
      }
    );
  };

  /**
   * Determina y retorna los datos de visualización del estado de factura
   * @returns Objeto con icono, texto y clase CSS para mostrar el estado de factura
   */
  const getInvoiceDisplayData = () => {
    const hasInvoiceUploaded = invoiceUrl && uploadedInvoiceDate;

    const invoiceStatusCases = [
      {
        condition: hasInvoiceUploaded,
        result: {
          icon: <IconEncircledCheckmark className="IconEncircledCheckmark" />,
          text: `Cargada ${formatShortDate(uploadedInvoiceDate!)?.onlyDate}`,
          classname: 'green'
        }
      }
    ];

    const matchedCase = invoiceStatusCases.find(caseItem => caseItem.condition);
    return (
      matchedCase?.result || {
        icon: <IconTimeClock className="IconTimeClock" />,
        text: 'Pendiente por cargar',
        classname: ''
      }
    );
  };

  const displayData = getPaymentDisplayData();
  const invoiceDisplayData = invoiceShow ? getInvoiceDisplayData() : null;
  const casesToNotShowPaymentInformation = !displayData || isPayedWithNudosCareAndOrderDetailsView;

  if (casesToNotShowPaymentInformation && !invoiceDisplayData) return null;

  const orderPaymentInformationStyles = `orderPaymentInformation ${isOrderDetailsView ? 'orderDetailsView' : ''}`;

  const renderStatusSection = (
    displayInfo: { icon: JSX.Element; text: string; classname: string; action?: () => void } | null,
    title: string,
    onClickAction?: () => void
  ) => {
    if (!displayInfo) return null;

    const statusContainerStyles = `paymentStatusContainer ${displayInfo.classname} ${
      isOrderDetailsView ? 'orderDetailsViewContainer' : ''
    }`;

    const handleClick = displayInfo.action || onClickAction;

    return (
      <div className={orderPaymentInformationStyles} onClick={handleClick}>
        {isOrderDetailsView && <div className="paymentStatusTitle">{title}</div>}
        <div className={statusContainerStyles}>
          {displayInfo.icon}
          <div className="text">{displayInfo.text}</div>
        </div>
      </div>
    );
  };

  return (
    <>
      {!casesToNotShowPaymentInformation && !invoiceDisplayData && renderStatusSection(displayData, 'Estado del pago:')}
      {!casesToNotShowPaymentInformation &&
        invoiceDisplayData &&
        renderStatusSection(invoiceDisplayData, 'Estado de la factura:')}
    </>
  );
};

export default OrderPaymentInformation;
